import { Provider, ProviderEvent } from './provider';
import {ConsentActionEvent, ConsentActionStatus, ConsentModeStatus} from '../../../consent/types';
import { TrackingConfig } from '../../types';

export declare namespace GaProvider {
  type CustomDimensions = Record<keyof GaProvider['customDimensions'], string>;
  type EventName =
    | 'click_start'
    | 'page_loaded'
    | 'ad_loaded'
    | 'click_answer'
    | 'click_skip'
    | 'click_back'
    | 'click_link_internal'
    | 'click_link_external';
  type EventCategory =
    | ''
    | 'Ads'
    | 'Result'
    | 'Question Details'
    | 'General'
    | 'Start Page'
    | 'Question Page'
    | 'Ad Page'
    | 'Result Page'
    | 'Riskfactor Page'
    | 'Tip Page';

  interface CommonEventParameters {
    event_category: EventCategory;
    event_label: string;
  }

  type EventParameters = CommonEventParameters & Partial<CustomDimensions>;

  interface Event extends ProviderEvent {
    type: string;
    name: EventName;
    parameters: EventParameters;
  }
}

export class GaProvider extends Provider {
  public trackingConfig: TrackingConfig;
  protected providerConfigured: boolean = false;
  protected gtag: Gtag.Gtag;
  private _sessionID: string = Math.floor(Date.now() * Math.random()).toString();

  public static get is(): string {
    return 'ga';
  }

  public constructor(trackingConfig: TrackingConfig) {
    super();

    this.trackingConfig = trackingConfig;

    this.initialize();
  }

  protected initialize() {
    this._initializeDataLayer();
    this._updateOptInStatus('Rejected');
    this._consentListeners();
  }

  // Initializes the dataLayer object but doesn't trigger any tags since GTAG script is not loaded yet
  private _initializeDataLayer() {
    window.dataLayer = window.dataLayer || [];

    this.gtag = function () {
      window.dataLayer.push(arguments);
    };

    // Set the default Consent Mode v2 configuration
    this.gtag("consent", "default", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
      wait_for_update: 500
    });

    this.gtag('js', new Date());
  }

  private _consentListeners() {
    if (window.consentActions) {
      if (window.consentActions['google_analytics']) {
        this._updateConsentStatus(window.consentActions['google_analytics']);
      }
    }

    const onConsentAction = (event: ConsentActionEvent) => {
      if (event.detail.vendor === 'google_analytics') {
        this._updateConsentStatus(event.detail.status);
      }
    };

    document.addEventListener('ndConsentAction', onConsentAction);
  }

  protected controllerImplementation() {
    const script = document.createElement('script') as HTMLScriptElement;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this._propertyId}`;
    document.head.appendChild(script);
  }

  private _updateOptInStatus(status: ConsentActionStatus) {
    const granted = status === 'Granted';
    const expirationDate = granted
      ? 'Thu, 01 Jan 1970 00:00:00 UTC'
      : 'Thu, 31 Dec 2099 23:59:59 UTC';
    const expiresStr = '; expires=' + expirationDate;
    const gaDisableStr = `ga-disable-${this._propertyId}`;

    // Set a cookie too for future use and in case an external service uses it
    document.cookie = gaDisableStr + '=true' + expiresStr + '; path=/';
    (window as any)[gaDisableStr] = !granted;

    // Update Consent Mode v2
    const consentModeStatus = ConsentModeStatus[status] || 'denied';
    this.gtag('consent', 'update', {
      ad_user_data: consentModeStatus,
      ad_personalization: consentModeStatus,
      ad_storage: consentModeStatus,
      analytics_storage: consentModeStatus
    });

    // Pause or resume eventsQueue tracking but only if the GA4 property is already configured
    if (this.providerConfigured) {
      if (granted) {
        this.ready();
        return;
      }

      this.pause();
    }
  }

  private _updateConsentStatus(status: ConsentActionStatus) {
    const granted = status === 'Granted';

    // If the user rejects and wasn't opted in before (the GA property wasn't configured), we don't need to do anything
    if (!this.providerConfigured && !granted) {
      return;
    }

    this._updateOptInStatus(status);

    if (!this.providerConfigured) {
      this.controllerImplementation();

      this.gtag('config', this._propertyId, {
        send_page_view: false,
        ...(this.trackingConfig.environment.toLowerCase() === 'stage' && {debug_mode: true})
      });

      // We need to send a separate `page_view` event if we need custom dimensions associated with it in GA4
      this.gtag('event', 'page_view', {
        ...this.customDimensions
      });

      this.providerConfigured = true;
      this.ready();
    }
  }

  protected trackingImplementation(event: GaProvider.Event) {
    const formattedEvent =
      event.type === 'event' ? this._formatEvent(event) : event;

    this.gtag(
      formattedEvent.type,
      formattedEvent.name,
      formattedEvent.parameters
    );
  }

  private _formatEvent(event: GaProvider.Event) {
    const formattedEvent = JSON.parse(JSON.stringify(event));

    // We need to push the custom dimensions with every event, the parameters can be overridden by passing the
    // desired value in the `GaProvider.EventParameters`
    const defaultParameters = {
      ...this.customDimensions,
    };

    formattedEvent.parameters = {
      ...defaultParameters,
      ...formattedEvent.parameters,
    };

    return formattedEvent;
  }

  private get _propertyId(): string {
    const propertyIdMap = new Map([
      ['production', 'G-K7PBKZCETJ'],
      ['stage', 'G-TPPL949Z7D'],
    ]);

    return (
      propertyIdMap.get(this.trackingConfig.environment.toLowerCase()) ||
      (propertyIdMap.get('stage') as string)
    );
  }

  public get customDimensions() {
    return {
      topic: this.trackingConfig.topic,
      customer: this.trackingConfig.customer,
      sessionID: this._sessionID,
      // 'timestamp': Date.now().toString()
    } as const;
  }
}
